import { paths } from 'src/routes/paths';

import { parseJobQueryWithWebLLM } from '../webllm/client';
import { rankJobs, applyFilters, fuzzySearchJobs } from '../utils';

import type { PreparedJobForLLM } from '../utils';
import type { SearchProvider, ProviderSearchResult } from './types';

type CreateJobsSearchProviderArgs = {
  jobs: PreparedJobForLLM[];
};

function mapJobsToResults(
  jobsToMap: PreparedJobForLLM[],
  group: string,
  scoreByIndex?: (index: number) => number
): ProviderSearchResult[] {
  return jobsToMap.map((job, index) => ({
    id: `job:${job.id}`,
    title: job.title,
    subtitle: [job.location.city, job.location.country].filter(Boolean).join(', ') || undefined,
    path: `${paths.jobs.jobDetails}?jobId=${job.id}`,
    group,
    weight: Math.max(10 - index, 6),
    fuseText: [
      job.title,
      ...job.skills,
      job.description,
      job.location.city,
      job.location.country,
      job.jobMode,
      job.jobType,
    ]
      .filter(Boolean)
      .join(' '),
    source: 'jobs',
    score: scoreByIndex ? scoreByIndex(index) : undefined,
  }));
}

export function createJobsSearchProvider({
  jobs,
}: CreateJobsSearchProviderArgs): SearchProvider {
  return {
    name: 'jobs-search-provider',
    canHandle: (context) => context.intent === 'jobs',
    fuseSearch: async ({ query, shouldCancel }) => {
      if (shouldCancel?.()) return [];

      const fuzzyMatches = fuzzySearchJobs(jobs, query).slice(0, 5);
      if (!fuzzyMatches.length) return [];
      if (shouldCancel?.()) return [];

      const matchedJobs = fuzzyMatches.map((match) => match.job);
      return mapJobsToResults(
        matchedJobs,
        '',
        (index) => 1 - (fuzzyMatches[index]?.score ?? 1)
      );
    },
    aiSearch: async ({ query, shouldCancel }) => {
      if (shouldCancel?.()) return [];

      const parsed = await parseJobQueryWithWebLLM(query, shouldCancel);
      if (shouldCancel?.()) return [];

      const filteredJobs = applyFilters(jobs, parsed.filters);
      const rankedJobs = rankJobs(filteredJobs, parsed.keywords).slice(0, 5);
      if (!rankedJobs.length) return [];
      if (shouldCancel?.()) return [];

      const results = mapJobsToResults(rankedJobs, 'Jobs · Semantic match', (index) => 1 / (index + 1));
      if (shouldCancel?.()) return [];

      return results;
    },
  };
}
